import React from 'react';
import { Star, XCircle } from 'lucide-react';

// Extracted from WorkerDashboard.js — receives everything it needs via `ctx`.
export default function WorkerRatingModal({ ctx }) {
  const { handleSubmitRating, label, ratingData, ratingLoading, setRatingData, setShowRatingModal, showRatingModal } = ctx;
  return (
    <>
    {/* Rating Modal */}
    {showRatingModal && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="rating-modal">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
          <div className="p-5 bg-gradient-to-r from-amber-500 to-orange-500 text-white flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg">Rate This Job</h3>
                <p className="text-sm text-white/80">{showRatingModal.title} — {showRatingModal.employer_name || 'Employer'}</p>
              </div>
              <button onClick={() => setShowRatingModal(null)} className="p-1.5 hover:bg-white/20 rounded-lg"><XCircle className="w-5 h-5" /></button>
            </div>
          </div>
          <div className="p-5 space-y-5 overflow-y-auto flex-1">
            {/* Overall Experience (auto-calculated) */}
            {(() => {
              const cats = [ratingData.management, ratingData.work_environment, ratingData.resources_available, ratingData.communication];
              const filled = cats.filter(c => c > 0);
              const overall = filled.length > 0 ? (filled.reduce((a, b) => a + b, 0) / filled.length).toFixed(1) : '—';
              return (
                <div className="text-center py-3 bg-amber-50 rounded-xl border border-amber-200">
                  <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-1">Overall Experience</p>
                  <p className="text-4xl font-bold text-amber-600" data-testid="overall-rating">{overall}</p>
                  <div className="flex items-center justify-center gap-1 mt-1">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} className={`w-4 h-4 ${filled.length > 0 && s <= Math.round(parseFloat(overall) || 0) ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} />
                    ))}
                  </div>
                  <p className="text-[10px] text-amber-500 mt-1">Based on category ratings</p>
                </div>
              );
            })()}

            {/* Rate by Category */}
            <div className="space-y-4">
              <p className="text-sm font-semibold text-slate-700">Rate by Category</p>
              {[
                { key: 'management', label: 'Management' },
                { key: 'work_environment', label: 'Work Environment' },
                { key: 'resources_available', label: 'Resources Available' },
                { key: 'communication', label: 'Communication' },
              ].map(cat => (
                <div key={cat.key}>
                  <p className="text-xs font-medium text-slate-600 mb-1.5">{cat.label}</p>
                  <div className="flex items-center gap-1.5">
                    {[1,2,3,4,5].map(s => (
                      <button
                        key={s}
                        onClick={() => setRatingData(prev => ({ ...prev, [cat.key]: s }))}
                        className="p-0.5 transition hover:scale-110"
                        data-testid={`rating-${cat.key}-${s}`}
                      >
                        <Star className={`w-7 h-7 ${s <= ratingData[cat.key] ? 'text-amber-500 fill-amber-500' : 'text-gray-300 hover:text-amber-300'}`} />
                      </button>
                    ))}
                    <span className="ml-2 text-sm text-slate-500">{ratingData[cat.key] > 0 ? `${ratingData[cat.key]}/5` : ''}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Written Review */}
            <div>
              <p className="text-xs font-medium text-slate-600 mb-1.5">Review (optional)</p>
              <textarea
                value={ratingData.review}
                onChange={e => setRatingData(prev => ({ ...prev, review: e.target.value }))}
                placeholder="Share your experience..."
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                data-testid="rating-review-input"
              />
            </div>

            {/* Would you work again? */}
            <div>
              <p className="text-xs font-medium text-slate-600 mb-2">Would you work with this employer again?</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setRatingData(prev => ({ ...prev, would_work_again: true }))}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition ${ratingData.would_work_again === true ? 'bg-emerald-50 border-emerald-400 text-emerald-700' : 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300'}`}
                  data-testid="would-work-again-yes"
                >
                  Yes
                </button>
                <button
                  onClick={() => setRatingData(prev => ({ ...prev, would_work_again: false }))}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition ${ratingData.would_work_again === false ? 'bg-red-50 border-red-400 text-red-700' : 'bg-white border-slate-200 text-slate-600 hover:border-red-300'}`}
                  data-testid="would-work-again-no"
                >
                  No
                </button>
              </div>
            </div>

            {/* Anonymous checkbox */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={ratingData.anonymous}
                onChange={e => setRatingData(prev => ({ ...prev, anonymous: e.target.checked }))}
                className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                data-testid="rating-anonymous-checkbox"
              />
              <span className="text-sm text-slate-600">Submit anonymously</span>
            </label>
          </div>

          {/* Footer */}
          <div className="px-5 pb-5 flex gap-3 flex-shrink-0">
            <button
              onClick={() => setShowRatingModal(null)}
              className="flex-1 py-2.5 rounded-lg font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition"
              data-testid="rating-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitRating}
              disabled={ratingLoading}
              className="flex-1 py-2.5 rounded-lg font-medium text-white bg-amber-500 hover:bg-amber-600 transition disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="rating-submit-btn"
            >
              {ratingLoading ? 'Submitting...' : 'Submit Rating'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
